import { EventEmitter } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';

export interface DstTableFilterMeta {
  value: any;
  matchMode: string;
}

export interface DstFilterEvent<T> {
  filteredValue: T[];
}

/**
 * MatTableDataSource extension with a PrimeNG p-table-compatible filter API so
 * existing DST grid components can migrate without rewriting filter handlers.
 */
export class DstMatTableDataSource<T> extends MatTableDataSource<T> {
  filters: Record<string, DstTableFilterMeta> = {};
  globalFilterFields: string[] = [];
  onFilter = new EventEmitter<DstFilterEvent<T>>();

  private globalFilterValue = '';
  private globalFilterMatchMode = 'contains';

  constructor(initialData: T[] = []) {
    super(initialData);
    this.filterPredicate = () => true;
  }

  get filteredValue(): T[] {
    return this.filteredData;
  }

  applyColumnFilter(value: any, field: string, matchMode: string): void {
    if (this.isEmptyFilterValue(value)) {
      delete this.filters[field];
    } else {
      this.filters[field] = { value, matchMode };
    }
    this.applyFilters();
  }

  applyGlobalFilter(value: string, matchMode: string): void {
    this.globalFilterValue = value || '';
    this.globalFilterMatchMode = matchMode;
    this.applyFilters();
  }

  resetFilters(): void {
    this.filters = {};
    this.globalFilterValue = '';
    this.globalFilterMatchMode = 'contains';
    this.applyFilters();
  }

  private applyFilters(): void {
    this.filterPredicate = (data: T) => this.matchesAllFilters(data);
    this.filter = JSON.stringify({
      filters: this.filters,
      global: this.globalFilterValue,
    });
    this.onFilter.emit({ filteredValue: this.filteredData });
  }

  private matchesAllFilters(data: T): boolean {
    for (const field of Object.keys(this.filters)) {
      const meta = this.filters[field];
      if (!this.matchesField(data, field, meta.value, meta.matchMode)) {
        return false;
      }
    }

    if (this.globalFilterValue) {
      const globalMatch = this.globalFilterFields.some((field) =>
        this.matchesField(data, field, this.globalFilterValue, this.globalFilterMatchMode)
      );
      if (!globalMatch) {
        return false;
      }
    }

    return true;
  }

  private matchesField(data: T, field: string, value: any, matchMode: string): boolean {
    const fieldValue = (data as Record<string, unknown>)[field];

    if (matchMode === 'in') {
      const values = Array.isArray(value) ? value : [value];
      if (values.length === 0) {
        return true;
      }
      const normalizedField = fieldValue != null ? String(fieldValue) : '';
      return values.some((candidate) => {
        const compareVal = candidate != null ? String(candidate) : '';
        return normalizedField === compareVal;
      });
    }

    const filterStr = String(value).toLowerCase();
    const strValue = fieldValue != null ? String(fieldValue).toLowerCase() : '';

    if (matchMode === 'contains') {
      return strValue.includes(filterStr);
    }
    if (matchMode === 'startsWith') {
      return strValue.startsWith(filterStr);
    }

    return true;
  }

  private isEmptyFilterValue(value: any): boolean {
    if (value === null || value === undefined || value === '') {
      return true;
    }
    return Array.isArray(value) && value.length === 0;
  }
}
